import { useState } from "react";

import { DateTime } from "luxon";

import { ArrowPathIcon, ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";

import { useAuth } from "../../contexts/AuthContext";
import useInitialPriceEstimate from "../../data/useInitialPriceEstimate";
import { IInitialPriceEstimate, IInitialPriceEstimateCategory } from "../../models/InitialPriceEstimate";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import UnlistedIntelligenceModal from "../ebayListings/unlistedTable/UnlistedIntelligenceModal";
import useNotification from "../notifications/useNotifications";
import LoadingWheel from "../shared/LoadingWheel";

const money = (value: number | null) => (value == null ? "—" : PriceFormat(value));

/**
 * Initial Price Estimate on a lead or quote: the live eBay asking prices for the part categories
 * set up in Admin, searched from this vehicle. Runs automatically when the lead arrives; Run / Re-run
 * starts a fresh search. Renders nothing unless the server says the feature is available.
 */
export default function InitialPriceEstimateCard({ leadId }: { leadId: string }) {
  const { addNotification } = useNotification();
  const { claims } = useAuth();
  const { availability, enabled, estimates, latest, isPending, run } = useInitialPriceEstimate(
    leadId,
    "InitialPriceEstimateCard",
  );
  const [viewing, setViewing] = useState<IInitialPriceEstimateCategory | null>(null);

  if (!enabled) {
    // Staff never see a card they cannot use. Admins are told why, so a missing migration, sync or
    // feature can be told apart without opening the network tab.
    if (!claims?.isAdmin || availability.isLoading) return null;

    const reason = availability.isError
      ? `Could not check availability: ${(availability.error as Error)?.message ?? "unknown error"}. Is the API up to date?`
      : availability.data?.data?.reason ?? "Not available for this customer.";

    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-white px-4 py-3 text-sm text-gray-600">
        <p className="flex items-center gap-2 font-medium text-gray-700">
          <SparklesIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          Initial Price Estimate is off
          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-normal text-gray-500">Admins only</span>
        </p>
        <p className="mt-1 text-xs">{reason}</p>
      </div>
    );
  }

  const autoLookup = availability.data?.data?.autoLookup ?? false;
  const showRejected = availability.data?.data?.showRejected ?? false;

  const handleRun = () =>
    run.mutate(undefined, {
      onSuccess: (res) => {
        if (res?.message) addNotification({ primaryText: res.message, variant: "success" });
      },
      onError: (error: any) =>
        addNotification({ primaryText: error?.message || "Could not start the estimate", variant: "error" }),
    });

  const runButton = (label: string) => (
    <button
      type="button"
      onClick={handleRun}
      disabled={run.isLoading || isPending}
      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
    >
      <ArrowPathIcon className={`h-4 w-4 ${run.isLoading ? "animate-spin" : ""}`} />
      {label}
    </button>
  );

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-6">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
          <div>
            <h2 className="text-lg font-medium leading-6 text-gray-900">Initial Price Estimate</h2>
            <p className="text-xs text-gray-500">Live market asking prices for this vehicle's main parts</p>
          </div>
        </div>
        {latest && !isPending && latest.status !== "Failed" && runButton("Re-run")}
      </div>

      <div className="px-4 py-4 sm:px-6">
        {estimates.isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingWheel />
          </div>
        ) : estimates.isError ? (
          <p className="text-sm text-red-700">Could not load the price estimate.</p>
        ) : !latest ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <p className="text-sm text-gray-600">No estimate has been run for this lead yet.</p>
            {!autoLookup && (
              <p className="text-xs text-gray-500">
                Automatic lookups are off for this account, so new leads are only priced when you press Run.
              </p>
            )}
            {runButton("Run estimate")}
          </div>
        ) : isPending ? (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-600">
            <ArrowPathIcon className="h-4 w-4 animate-spin text-amber-500" />
            {latest.status === "Queued" ? "Queued — the price checks will start shortly…" : "Checking prices…"}
          </div>
        ) : latest.status === "Failed" ? (
          <div className="flex flex-col gap-3 rounded-md border border-red-200 bg-red-50 p-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 text-sm text-red-800">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{latest.error || "The estimate could not be completed."}</span>
            </div>
            {runButton("Retry")}
          </div>
        ) : (
          <EstimateResults estimate={latest} onView={setViewing} />
        )}
      </div>

      <UnlistedIntelligenceModal
        open={!!viewing}
        setOpen={(open) => {
          const next = typeof open === "function" ? open(!!viewing) : open;
          if (!next) setViewing(null);
        }}
        results={viewing?.results ?? null}
        loading={false}
        title={`${viewing?.categoryName ?? ""} — matching listings`}
        searchTerm={viewing?.searchTerm}
        showExcluded={showRejected}
      />
    </div>
  );
}

function EstimateResults({
  estimate,
  onView,
}: {
  estimate: IInitialPriceEstimate;
  onView: (category: IInitialPriceEstimateCategory) => void;
}) {
  return (
    <>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500">Total of averages</p>
          <p className="text-2xl font-semibold tabular-nums text-gray-900">{money(estimate.totalAverage)}</p>
        </div>
        <p className="text-xs text-gray-500">
          {estimate.pricedCategoryCount} of {estimate.categoryCount} categories priced
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3 font-medium">Category</th>
              <th className="py-2 pr-3 font-medium text-right">Listings</th>
              <th className="py-2 pr-3 font-medium text-right">Min</th>
              <th className="py-2 pr-3 font-medium text-right">Avg</th>
              <th className="py-2 pr-3 font-medium text-right">Max</th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {estimate.categories.map((category) => (
              <tr key={category.partCategoryId}>
                <td className="py-2 pr-3">
                  <p className="font-medium text-gray-900">{category.categoryName}</p>
                  <p className="max-w-[16rem] truncate text-xs text-gray-500" title={category.searchTerm}>
                    {category.searchTerm}
                  </p>
                  {category.error && <p className="text-xs text-red-700">{category.error}</p>}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{category.listingCount}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{money(category.minPrice)}</td>
                <td className="py-2 pr-3 text-right tabular-nums font-semibold text-gray-900">{money(category.averagePrice)}</td>
                <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{money(category.maxPrice)}</td>
                <td className="py-2 text-right">
                  {category.listingCount > 0 && (
                    <button
                      type="button"
                      onClick={() => onView(category)}
                      className="whitespace-nowrap text-xs font-medium text-amber-700 hover:underline"
                    >
                      View matches
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        {estimate.trigger === "Manual" ? "Run by hand" : "Run automatically"}
        {estimate.completedOn ? ` on ${formatDateTime(estimate.completedOn, DateTime.DATETIME_SHORT)}` : ""}. Asking
        prices of live used listings, not sold prices.
      </p>
    </>
  );
}
